import { getLimitedUseToken, getToken } from "firebase/app-check";
import { useContext, useCallback } from "react";
import { AppCheckSdkContext } from "reactfire";
import { callFunction } from "./firebase";

// Plain request function for non-hook usage
export async function request(path, options = {}) {
  const method = options.method || "GET";
  let body = {};
  
  // Handle body - it could be a string (JSON) or an object
  if (options.body) {
    if (typeof options.body === 'string') {
      try {
        body = JSON.parse(options.body);
      } catch (e) {
        // If parsing fails, treat it as an empty object
        console.error('Failed to parse request body:', e);
        body = {};
      }
    } else if (typeof options.body === 'object') {
      body = options.body;
    }
  }
  
  const responseType = options.responseType || "json";
  
  return callFunction(path, body, method, {}, responseType);
}

function useGetAppCheckToken() {
  const sdk = useContext(AppCheckSdkContext);

  return useCallback(
    async function (useShortLivedToken) {
      try {
        if (!sdk) {
          return;
        }

        const { token } = await (useShortLivedToken
          ? getLimitedUseToken(sdk)
          : getToken(sdk, false));
        return token;
      } catch (e) {
        return;
      }
    },
    [sdk],
  );
}

export function useRequest() {
  const getAppCheckToken = useGetAppCheckToken();

  return useCallback(
    async function (path, body, isPost, responseType = "json", moduleName = "", isGet = false) {
      const token = await getAppCheckToken(isPost);

      const headers = {};
      if (token) {
        headers["x-firebase-appcheck"] = token;
      }

      // Ensure proper HTTP method
      const method = isGet ? "GET" : isPost ? "POST" : "GET";
      return callFunction(path, body || {}, method, headers, responseType, moduleName);
    },
    [getAppCheckToken],
  );
}

export function usePost() {
  const request = useRequest();
  return function (functionName, body = {}) {
    return request(functionName, body, true);
  };
}

export function useGet() {
  const request = useRequest();
  return function (functionName, body = {}, responseType) {
    return request(functionName, body, false, responseType);
  };
}

export function useVesselGet() {
  const request = useRequest();
  return function (functionName, body = {}, responseType) {
    return request(functionName, body, false, responseType, "vessel");
  };
}

export function useContainersGet() {
  const request = useRequest();
  return function (functionName, body = {}, responseType) {
    return request(functionName, body, false, responseType, "containers");
  };
}

export function useExport() {
  const request = useRequest();
  return function (functionName, body = {}, responseType) {
    return request(functionName, body, null, responseType, "export");
  };
}

export function useTradingPost() {
  const request = useRequest();
  return function (functionName, body = {}) {
    return request(functionName, body, true, "json", "trading");
  };
}

export function useTradingGet() {
  const request = useRequest();
  return function (functionName, body = {}, responseType) {
    return request(functionName, body, false, responseType, "trading", true);
  };
}
